/**
 * Revision History
 * 
 * 11/09/2021 ptkim     Add PasswordRenderer
 */
import numeral from 'numeral';
import React from 'react';

import CheckBox from '../ui/CheckBox';
import CheckBoxIcon from '../ui/CheckBoxIcon';
import {formatDate, formatDateTime, formatDateUtc} from '../dateUtils';


import {getDecimal, stringsEqualIgnoreCase} from '../util';
import PrimaryInverseButton from "../ui/PrimaryInverseButton";
import useSuntekSettings from '../hooks/useSuntekSettings';

interface IndexByType {
  [key: string]: number,
};

function NumberedIndicatorRendererImpl({row}:any) {
  return <div className="ifms-table-default">{row.index + 1}</div>;
}

function IndicatorRendererImpl() {
  return <div>&nbsp;</div>;
}

function TableIndicatorRendererImpl({rowIndex, onLabelClick, tooltip}:any) {
  let className = "ifms-table-default table-cell-disabled";
  if (!onLabelClick) {
    className += " ifms-table-indicator"
  }

  const clickProps = onLabelClick
    ? {
      onClick: onLabelClick,
      style: {
        cursor: 'pointer',
        color: 'blue',
        fontWeight: 600,
      }
    }
    : {};

  return <div className={className} data-tooltip={tooltip} {...clickProps} >{rowIndex + 1}</div>;
}

function TableCheckBoxRendererImpl({ rowIndex, columnId, value, disabled, onChangeFunc }: any) {
  return (
    <div className={disabled ? 'table-cell-disabled' : ''}>
      <CheckBox id={`${columnId}-${rowIndex}`} name={`${columnId}-${rowIndex}`} disabled={disabled} value={value} onChange={chk => onChangeFunc(chk)} />
    </div>
  );
}

function TableNumberRendererImpl({ rowIndex, columnId, value, disabled, align, decimal, showZero }: any) {
  const formatNumber = (value: number | null): string | null => {
    if (value === null) return null;

    const format = `0,0.${'0'.repeat(decimal)}`;
    const formattedValue = numeral(value).format(format);

    return (!showZero && Number(formattedValue) === 0 && disabled) ? null : formattedValue;
  };

  const formattedValue = formatNumber(value);

  let className = `ifms-table-align-${align} ifms-table-number`;
  if (disabled) {
    className += " table-cell-disabled"
  }

  return (
    <div id={`${columnId}-${rowIndex}`} className={className}>
      {formattedValue}
    </div>
  );
}

function TableTextRendererImpl({ rowIndex, columnId, value, disabled, align, onLabelClick }: any) {
  let className = `ifms-table-align-${align}`;
  if (disabled) {
    className += " table-cell-disabled"
  }

  const clickProps = onLabelClick
    ? {
      onClick: onLabelClick,
      style: {
        cursor: 'pointer',
        color: 'blue',
        fontWeight: 600,
      }
    }
    : {};

  return (
    <div
      {...clickProps}
      id={`${columnId}-${rowIndex}`}
      className={className}
    >
      <span>{value}</span>
    </div>
  );
}

function TableDateRendererImpl({ rowIndex, columnId, value, disabled, dateType }: any) {
  const suntekSettings = useSuntekSettings();
  const dateFormat = suntekSettings.company?.dateFormat.toUpperCase() || '';

  let className = 'ifms-table-align-center';
  if (disabled) {
    className += " table-cell-disabled"
  }

  let renderValue = null;
  if (dateType === 'dateonly') {
    renderValue = formatDate(value, dateFormat);
    className += " ifms-table-date"
  }
  else if (dateType === 'datetime') {
    renderValue = formatDateTime(value, dateFormat, false, true);
    className += " ifms-table-datetime"
  }
  else if (dateType === 'datetimesec') {
    renderValue = formatDateTime(value, dateFormat, true, true);
    className += " ifms-table-datetimesec"
  }

  return (
    <div
      id={`${columnId}-${rowIndex}`}
      className={className}
    >
      {renderValue}
    </div>
  );
}

function TextRendererImpl({row, cell}:any) {
  return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default">{cell.value}</div>;
}

function TextAlignLeftRendererImpl({row, cell, accountName}:any) {
  const displayValue = accountName ? accountName : cell.value;
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-left">
      {displayValue}
    </div>
  );
}

function TextAlignLeftWithBordersRendererImpl({cell}:any) {
  return (
    <div className="ifms-table-cell-left-border" style={{width: '100%'}} id={`${cell.column.id}-${cell.row.index}`}>
      {cell.value}
    </div>
  );
}

function TextAlignLeftWithAddressTooltipRendererImpl({ cell }: any) {
  let tempAddressText = '';
  switch (cell.column.Header) {
    case 'Shipper':
      //Air house = original.shipper / Ocean house = original.sName
      tempAddressText = cell.row.original.sName ? cell.row.original.sName : cell.row.original.shipper; 
      break;
    case 'Consignee':
      //Air house = original.consignee / Ocean house = original.cName
      tempAddressText = cell.row.original.cName ? cell.row.original.cName : cell.row.original.consignee;
      break;
    case 'Notify':
      //Air house = original.notify / Ocean house = original.nName
      tempAddressText = cell.row.original.nName ? cell.row.original.nName : cell.row.original.notify;
      break;
    case 'Customs Broker': //OEH
      tempAddressText = cell.row.original.bName;
      break;
    case 'P/U Cargo Location': //OE Booking
      tempAddressText = cell.row.original.puCargoLocation;
      break;
    case 'Trucking Vendor': //OE Booking
      tempAddressText = cell.row.original.truckingCompany;
      break;
  }
  return <div className="ifms-table-align-left" data-tooltip={tempAddressText} id={`${cell.column.id}-${cell.row.index}`}>{cell.value} </div>;
}

function TextAlignRightRendererImpl({cell, row}:any) {
  return (
      <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-right">{cell.value}</div>
  );
}

function CheckboxRendererImpl({cell}:any) {
  return <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
    <CheckBox inlist value={stringsEqualIgnoreCase('true', cell?.value?.toString())} disabled />
  </div>;
}

function CheckboxIconRendererImpl({cell}:any) {
  return <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
    <CheckBoxIcon value={cell?.value} />
  </div>;
}

function NullableCheckboxIconRendererImpl({row, cell}:any) {
  if (cell && cell.value) {
    return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default">
      <CheckBoxIcon value={true} />
    </div>;  
  }

  return <div className="ifms-table-default ifms-check-box-icon-false" id={`${cell.column.id}-${cell.row.index}`}></div>;
}

function NullableCheckboxRedIconRendererImpl({cell}:any) {
  if (cell && cell.value) {
    return <div id={`${cell.column.id}-${cell.row.index}`} className="ifms-table-default">
      <CheckBoxIcon value={true} color="red"/>
    </div>;  
  }

  return <div className="ifms-table-default ifms-check-box-icon-false" id={`${cell.column.id}-${cell.row.index}`}></div>;
}

function NullableDangerCheckboxIconRendererImpl({cell}:any) {
  if (cell && cell.value) {
    return <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
      <CheckBoxIcon value={true} color="red" />
    </div>;  
  }

  return <div className="ifms-table-default ifms-check-box-icon-false" id={`${cell.column.id}-${cell.row.index}`}></div>;
}

function NullableBlackCheckboxIconRendererImpl({cell}:any) {
  if (cell && cell.value) {
    return <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
      <CheckBoxIcon value={true} color="black" />
    </div>;  
  }

  return null;
}

function DateRendererImpl({cell, row}:any) {
const suntekSettings = useSuntekSettings();
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default ifms-table-date">
      {formatDate(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '')}
    </div>
  );
}

function DateUtcRendererImpl({cell, row}:any) {
  const suntekSettings = useSuntekSettings();
    return (
      <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default ifms-table-date">
        {formatDateUtc(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '')}
      </div>
    );
}

function DateTimeRendererImpl({cell, format}:any) {
  const suntekSettings = useSuntekSettings();
  return (
    <div className="ifms-table-default ifms-table-datetime" id={`${cell.column.id}-${cell.row.index}`}> 
      {formatDateTime(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '', false, true)}
    </div>
  );
}

function DateTimeIncludeSecRendererImpl({cell, format}:any) {
  const suntekSettings = useSuntekSettings();
  return (
    <div className="ifms-table-default ifms-table-datetimesec" id={`${cell.column.id}-${cell.row.index}`}>
      {formatDateTime(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '', true, true)}
    </div>
  );
}

function CurrencyRendererImpl({data, row, cell}:any) {
  const value = cell.value ? cell.value.currencyCode : '';
  return (
    <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
      {value}
    </div>
  );
}

function CurrencyAmountRendererImpl({currencyAccessor, data, row, cell}:any) {
  const currency = currencyAccessor(data[row.index]);
  let amount = '';
  const cellValue = cell.value;
  const decimals = getDecimal(currency);
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    const format = `0,0.${'0'.repeat(decimals)}`;
    amount = numeral(cellValue.toFixed(decimals)).format(format);
  }

  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-currency-amount">
      {amount}
      {/* <CurrencyLabel value={cell.value} currency={currency} showZero={true} /> */}
    </div>
  );
}

// 22.12.27 hykim - 정수의 자리 구분이 필요 없는 경우를 추가하기 위해 useComma를 추가함
// function FloatRendererImpl({decimals = 2, cell}:any) {
function FloatRendererImpl({useComma = true, decimals = 2, color = 'black', tooltipValue = '', disabled, row, cell}:any) {
  let amount = '';
  let formattedNumber = '';
  const cellValue = cell.value;
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    // const format = `'0,0'.${'0'.repeat(decimals)}`;
    const format = `${useComma ? '0,0' : '0'}.${'0'.repeat(decimals)}`;
    formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: useComma }).format(cellValue);
    amount = numeral(cellValue).format(format);
  }
  let className = "ifms-table-align-right  ifms-table-float"
  if(disabled) className += " table-cell-disabled"
  return (
    <div data-tooltip={tooltipValue} id={`${cell.column.id}-${row.index}`} style={{color: `${color}`}} className={className} >{amount === 'NaN' ? formattedNumber : amount}</div>
  );
}

function FloatCurRendererImpl({row, cell, useComma = true, currency = 'USD'}:any) {
  let amount = '';
  let formattedNumber = '';
  const decimals = getDecimal(currency);
  const cellValue = cell.value;
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    const format = `${useComma ? '0,0' : '0'}.${'0'.repeat(decimals)}`;
    formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: useComma }).format(cellValue);
    amount = numeral(cellValue).format(format);
  }
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-right" >{amount === 'NaN' ? formattedNumber : amount}</div>
  );
}

function FloatWithBorderRendererImpl({decimals = 2, cell}:any) {
  let amount = '';
  let formattedNumber = '';
  const cellValue = cell.value;
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    const format = `0,0.${'0'.repeat(decimals)}`;
    formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(cellValue);
    amount = numeral(cellValue).format(format);
  }
  return (
    <div className="ifms-table-cell-right-border">{amount === 'NaN' ? formattedNumber : amount}</div>
  );
}

function BlueTextRendererImpl({cell, tooltip = 'Click for Detail', bold = true, onClick}:any) {
  // return <span data-tooltip={tooltip} className="input-control-row-label-link">{cell.value}</span>
  return <PrimaryInverseButton style={{color: 'blue', fontWeight: bold ? 'bold' : 'normal', fontSize: '10px', padding: '0 !important'}} onClick={onClick} data-tooltip={tooltip}>{cell.value}</PrimaryInverseButton>
}

//<pt211109>
function PasswordRendererImpl({cell}: any) {
  return <div style={{marginTop: '0px'}}>{'*'.repeat(cell.value.length)}</div>;
}

export const NumberedIndicatorRenderer = React.memo(NumberedIndicatorRendererImpl);
export const IndicatorRenderer = React.memo(IndicatorRendererImpl);
export const TextRenderer = React.memo(TextRendererImpl);
export const TableTextRenderer = React.memo(TableTextRendererImpl);
export const TableDateRenderer = React.memo(TableDateRendererImpl);
export const TableNumberRenderer = React.memo(TableNumberRendererImpl)
export const TableCheckBoxRenderer = React.memo(TableCheckBoxRendererImpl)
export const TableIndicatorRenderer = React.memo(TableIndicatorRendererImpl)
export const CheckboxRenderer = React.memo(CheckboxRendererImpl);
export const CheckboxIconRenderer = React.memo(CheckboxIconRendererImpl);
export const NullableCheckboxIconRenderer = React.memo(NullableCheckboxIconRendererImpl);
export const NullableDangerCheckboxIconRenderer = React.memo(NullableDangerCheckboxIconRendererImpl);
export const NullableBlackCheckboxIconRenderer = React.memo(NullableBlackCheckboxIconRendererImpl);
export const DateRenderer = React.memo(DateRendererImpl);
export const DateTimeRenderer = React.memo(DateTimeRendererImpl);
export const DateTimeIncludeSecRenderer = React.memo(DateTimeIncludeSecRendererImpl);
export const CurrencyRenderer = React.memo(CurrencyRendererImpl);
export const TextAlignLeftRenderer = React.memo(TextAlignLeftRendererImpl);
export const TextAlignLeftWithBordersRenderer = React.memo(TextAlignLeftWithBordersRendererImpl);
export const TextAlignRightRenderer = React.memo(TextAlignRightRendererImpl);
export const CurrencyAmountRenderer = React.memo(CurrencyAmountRendererImpl);
export const TextAlignLeftWithAddressTooltipRenderer = React.memo(TextAlignLeftWithAddressTooltipRendererImpl);
export const FloatRenderer = React.memo(FloatRendererImpl);
export const FloatCurRenderer = React.memo(FloatCurRendererImpl);
export const FloatWithBorderRenderer = React.memo(FloatWithBorderRendererImpl);
export const PasswordRenderer = React.memo(PasswordRendererImpl);   //<pt211109>
export const BlueTextRenderer = React.memo(BlueTextRendererImpl);   //<pt211109>
export const NullableCheckboxRedIconRenderer = React.memo(NullableCheckboxRedIconRendererImpl);
export const DateUtcRenderer = React.memo(DateUtcRendererImpl);
